import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../context/AuthContext';
import CrimsonLogo from '../components/CrimsonLogo';
import { isEmailProvisioned } from '../firebase/orgs';

export default function LoginPage() {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode]       = useState('signin'); // 'signin' | 'signup' | 'reset'
  const [email, setEmail]     = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]     = useState('');
  const [info, setInfo]       = useState('');
  const [loading, setLoading] = useState(false);

  const handleGoogle = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      navigate('/');
    } catch (e) {
      const msg = e.message || '';
      if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('insufficient')) {
        setError('Your account hasn\'t been provisioned. Contact your organisation admin to get access.');
      } else {
        setError(msg || 'Google sign-in failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async (e) => {
    e.preventDefault();
    if (!email) return;
    setError('');
    setInfo('');
    setLoading(true);
    try {
      if (mode === 'reset') {
        await resetPassword(email);
        setInfo('Password reset email sent. Check your inbox.');
        return;
      }
      if (!password) return;
      if (mode === 'signup') {
        // Block account creation for emails not pre-approved by an admin
        const allowed = await isEmailProvisioned(email);
        if (!allowed) {
          setError('Your account hasn\'t been provisioned. Contact your organisation admin to get access.');
          return;
        }
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
      navigate('/');
    } catch (e) {
      const msg = e.message || '';
      if (msg.toLowerCase().includes('permission') || msg.toLowerCase().includes('insufficient')) {
        setError('Your account hasn\'t been provisioned. Contact your organisation admin to get access.');
      } else {
        setError(msg || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: '#060d18',
        padding: 24,
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 400,
          background: '#0f1923',
          border: '1px solid #1e3a5f',
          borderRadius: 12,
          padding: '40px 36px',
        }}
      >
        {/* Logo + branding */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <CrimsonLogo size={48} />
          <h1 style={{ color: '#e2e8f0', fontWeight: 700, fontSize: '1.5rem', marginTop: 12, marginBottom: 4 }}>
            CrimsonScan
          </h1>
          <p style={{ color: '#f43f5e', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 14 }}>
            Spectra
          </p>
          <p style={{ color: '#94a3b8', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            AI-powered materials discovery: from idea to candidate in minutes.
            Describe what you need, we route to the right models, rank results, and deliver patent-grade reports.
          </p>
        </div>

        {/* Google sign-in — only shown once Google auth is enabled in Firebase console */}
        {process.env.REACT_APP_GOOGLE_AUTH_ENABLED === 'true' && (
          <>
            <button
              onClick={handleGoogle}
              disabled={loading}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: '10px 16px',
                background: '#fff',
                color: '#1a1a1a',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                marginBottom: 20,
                opacity: loading ? 0.7 : 1,
              }}
            >
              <GoogleIcon />
              Continue with Google
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, height: 1, background: '#1e3a5f' }} />
              <span style={{ color: '#475569', fontSize: 12 }}>or</span>
              <div style={{ flex: 1, height: 1, background: '#1e3a5f' }} />
            </div>
          </>
        )}

        {/* Email/password form */}
        <form onSubmit={handleEmail} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            style={{
              background: '#0a1120',
              border: '1px solid #1e3a5f',
              borderRadius: 6,
              color: '#e2e8f0',
              padding: '10px 12px',
              fontSize: 14,
              outline: 'none',
              width: '100%',
            }}
          />
          {mode !== 'reset' && (
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{
                background: '#0a1120',
                border: '1px solid #1e3a5f',
                borderRadius: 6,
                color: '#e2e8f0',
                padding: '10px 12px',
                fontSize: 14,
                outline: 'none',
                width: '100%',
              }}
            />
          )}

          {error && (
            <p style={{ color: '#f87171', fontSize: 13, margin: 0 }}>{error}</p>
          )}
          {info && (
            <p style={{ color: '#4ade80', fontSize: 13, margin: 0 }}>{info}</p>
          )}

          <button
            type="submit"
            disabled={loading || !email || (mode !== 'reset' && !password)}
            style={{
              background: '#f43f5e',
              border: 'none',
              borderRadius: 6,
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              padding: '10px 16px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? 'Please wait...' : mode === 'reset' ? 'Send Reset Link' : mode === 'signin' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        {/* Forgot password (only in signin mode) */}
        {mode === 'signin' && (
          <p style={{ textAlign: 'right', marginTop: 8, marginBottom: 0 }}>
            <button
              onClick={() => { setMode('reset'); setError(''); setInfo(''); }}
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 12, padding: 0 }}
            >
              Forgot password?
            </button>
          </p>
        )}

        {/* Toggle signin/signup/reset */}
        <p style={{ color: '#64748b', fontSize: 13, textAlign: 'center', marginTop: mode === 'signin' ? 12 : 20, marginBottom: 0 }}>
          {mode === 'reset' ? 'Remember your password? ' : mode === 'signin' ? "Don't have an account? " : 'Already have an account? '}
          <button
            onClick={() => { setMode(mode === 'signup' ? 'signin' : mode === 'reset' ? 'signin' : 'signup'); setError(''); setInfo(''); }}
            style={{ background: 'none', border: 'none', color: '#38bdf8', cursor: 'pointer', fontSize: 13, padding: 0 }}
          >
            {mode === 'reset' ? 'Sign in' : mode === 'signin' ? 'Sign up' : 'Sign in'}
          </button>
        </p>
      </div>

      {/* Enrollment CTA — visible to anyone, no login required */}
      <div style={{ marginTop: 24, textAlign: 'center' }}>
        <p style={{ color: '#475569', fontSize: 13, marginBottom: 10 }}>
          New to CrimsonScan?
        </p>
        <button
          onClick={() => navigate('/enroll?intent=pilot')}
          style={{
            background: 'transparent',
            border: '1px solid #f43f5e',
            borderRadius: 6,
            color: '#f43f5e',
            fontWeight: 600,
            fontSize: 14,
            padding: '10px 28px',
            cursor: 'pointer',
            width: '100%',
            maxWidth: 400,
          }}
        >
          Talk to us
        </button>
        <p style={{ color: '#334155', fontSize: 11, marginTop: 10 }}>
          Three-month paid pilot on your own program
        </p>
      </div>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 48 48">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    </svg>
  );
}
